import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { gql, useMutation } from "@apollo/client";
import { Box, CircularProgress, Typography } from "@mui/material";
import { getSiteGraphqlUrl } from "@hooks/use-site-context/utils";
import { setLocalStorageItem } from "../../utils";
import { USER_SESSION_KEY } from "../../hooks/use-auth-context/utils";
import { normalizeCapabilities } from "../../hooks/use-auth-context/utils";
import { UserRoles } from "../../hooks/use-auth-context/types";
import { Views } from "../../utils/types";
import { colors, shadows, whiteTint } from "../../utils/colors";

// ── GraphQL mutations ─────────────────────────────────────────────────────────

const LOGIN_WITH_GOOGLE = gql`
  mutation LoginWithGoogle($code: String!, $state: String) {
    login(
      input: {
        provider: GOOGLE
        oauthResponse: { code: $code, state: $state }
      }
    ) {
      authToken
      authTokenExpiration
      refreshToken
      refreshTokenExpiration
      user {
        roles { nodes { name } }
        firstName
        lastName
        email
        capabilities
      }
    }
  }
`;

const LOGIN_WITH_MICROSOFT = gql`
  mutation LoginWithMicrosoft($code: String!, $state: String) {
    login(
      input: {
        provider: OAUTH2_GENERIC
        oauthResponse: { code: $code, state: $state }
      }
    ) {
      authToken
      authTokenExpiration
      refreshToken
      refreshTokenExpiration
      user {
        roles { nodes { name } }
        firstName
        lastName
        email
        capabilities
      }
    }
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────

export const AuthCallbackView = () => {
    const { provider } = useParams<{ provider: string }>();
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);
    const hasRun = useRef(false);

    // Parse our embedded school slug from the OAuth state.
    // State format: "<plugin_state>|<school_slug>"
    const rawState = new URLSearchParams(window.location.search).get("state") ?? "";
    const pipeIdx = rawState.lastIndexOf("|");
    const pluginState = pipeIdx !== -1 ? rawState.slice(0, pipeIdx) : rawState;
    const returnSchool = pipeIdx !== -1 ? rawState.slice(pipeIdx + 1) : "";

    // The current subdomain (empty string when on root domain like "localhost").
    const currentSchool = window.location.hostname.split(".")[0];
    const isOnCorrectSubdomain = returnSchool && returnSchool === currentSchool;

    // Use the current subdomain's GraphQL URL — this is what's correct when we
    // are on the school subdomain after re-routing (see effect below).
    const mutation = provider === "google" ? LOGIN_WITH_GOOGLE : LOGIN_WITH_MICROSOFT;

    const [loginWithProvider] = useMutation(mutation, {
        context: { uri: getSiteGraphqlUrl() },
    });

    useEffect(() => {
        if (hasRun.current) return;
        hasRun.current = true;

        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");

        if (!code) {
            setError("No authorization code received. Please try signing in again.");
            return;
        }

        if (provider !== "google" && provider !== "microsoft") {
            setError("Unknown provider. Please try signing in again.");
            return;
        }

        // If we're on the root domain (or wrong subdomain), re-route to the
        // school subdomain. The exchange happens there so the JWT is stored in
        // the correct localStorage origin and the app can read it immediately.
        if (returnSchool && !isOnCorrectSubdomain) {
            const parts = window.location.hostname.split(".");
            const base = parts.slice(-(parts[parts.length - 1] === "localhost" ? 1 : 2)).join(".");
            const port = window.location.port ? `:${window.location.port}` : "";
            window.location.replace(
                `${window.location.protocol}//${returnSchool}.${base}${port}/auth/${provider}/callback${window.location.search}`
            );
            return;
        }

        // We're on the correct school subdomain — exchange the code for a JWT.
        const state = pluginState || undefined;
        loginWithProvider({ variables: { code, state } })
            .then(({ data }) => {
                const session = data?.login;
                if (!session?.authToken) {
                    setError("Sign-in failed. Please try again.");
                    return;
                }

                const caps = normalizeCapabilities(session.user?.capabilities);
                setLocalStorageItem(USER_SESSION_KEY, {
                    ...session,
                    firstName: session.user?.firstName,
                    lastName: session.user?.lastName,
                    email: session.user?.email,
                    role: session.user?.roles?.nodes?.[0]?.name ?? UserRoles.Student,
                    capabilities: caps,
                });

                navigate(Views.ASSET_TABLE, { replace: true });
            })
            .catch(() => {
                setError("Sign-in failed. Please try again.");
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box
            sx={{
          width: "100%",
          minHeight: "100vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
          background: `linear-gradient(160deg, ${colors.navy} 0%, ${colors.navyDeep} 100%)`,
          px: 2,
            }}
        >
        <Box
          sx={{
            width: "100%",
            maxWidth: 420,
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            border: `1px solid ${whiteTint(0.08)}`,
            boxShadow: shadows.heroCard,
            bgcolor: colors.surface,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "center",
          }}
        >
          {error ? (
            <Typography color="error" variant="body1" textAlign="center" maxWidth={360}>
              {error}
            </Typography>
          ) : (
            <>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Completing sign-in...
              </Typography>
            </>
          )}
        </Box>
        </Box>
    );
};

export default AuthCallbackView;
